import { useEffect, useMemo, useState } from "react";
import {
  Check,
  ChevronRight,
  Coins,
  Gift,
  Heart,
  House,
  Landmark,
  Leaf,
  PawPrint,
  Plus,
  Search,
  Send,
  ShieldCheck,
  Sparkles,
  X
} from "lucide-react";
import {
  CATALOG_COUNTS,
  formatMoney,
  getCatalogItem,
  getHomeType,
  HOME_CHANGE_FEE_COPPER,
  HOME_TYPES,
  normalizeMoney,
  PetSpeciesSchema,
  projectRatio,
  projectStageIndex,
  validateQuestCopy,
  type ItemCategory,
  type PetSpecies
} from "@tribe/domain";
import { buildOwnerNames, ownerNameFrom } from "./ownerNames";
import { YardEditor } from "./YardEditor";
import { InteriorEditor } from "./InteriorEditor";
import { Forecast } from "./Forecast";
import { type PanelId, useVillageStore } from "./store";
import { apiMutation, apiQuery, isLive, returnToDiscord, sendPresence } from "./api";

// Taken from the schema rather than written out again: this list was a hand-kept copy, and it
// silently went stale the moment sheep were added to the catalogue.
const petSpecies: PetSpecies[] = PetSpeciesSchema.options;
// Homes are not shopped for. They unlock together with the first quest and are chosen in the
// home panel, where the change fee is shown against the one you already live in.
const shopCategories: Array<{ id: ItemCategory; label: string }> = [
  { id: "furniture", label: "Furniture" },
  { id: "exterior", label: "Outdoors" },
  { id: "plant", label: "Plants" },
  { id: "pet", label: "Pets" },
  { id: "rare", label: "Rare" }
];

function PanelShell({ title, eyebrow, children, onClose }: { title: string; eyebrow?: string; children: React.ReactNode; onClose?: () => void }) {
  const setPanel = useVillageStore((state) => state.setPanel);
  return (
    <aside className="context-panel" aria-label={title}>
      <header className="panel-header">
        <div>
          {eyebrow && <span className="eyebrow">{eyebrow}</span>}
          <h2>{title}</h2>
        </div>
        <button className="icon-button" aria-label={`Close ${title}`} onClick={() => onClose ? onClose() : setPanel(null)}><X size={20} /></button>
      </header>
      <div className="panel-scroll">{children}</div>
    </aside>
  );
}

function MoneyLabel({ value }: { value: number }) {
  const money = normalizeMoney(value);
  return (
    <span className="price" aria-label={formatMoney(value)}>
      {money.gold > 0 && <span><i className="coin coin-gold" />{money.gold}</span>}
      {money.silver > 0 && <span><i className="coin coin-silver" />{money.silver}</span>}
      {(money.copper > 0 || value === 0) && <span><i className="coin coin-copper" />{money.copper}</span>}
    </span>
  );
}

function EmptyState({ icon, title, copy, action }: { icon: React.ReactNode; title: string; copy: string; action?: React.ReactNode }) {
  return (
    <div className="empty-state">
      <div className="empty-icon">{icon}</div>
      <h3>{title}</h3>
      <p>{copy}</p>
      {action}
    </div>
  );
}

function VillagePanel() {
  const data = useVillageStore((state) => state.data);
  const selectedPlotId = useVillageStore((state) => state.selectedPlotId);
  const setPanel = useVillageStore((state) => state.setPanel);
  const interiorPhase = useVillageStore((state) => state.interiorPhase);
  const [memberQuery, setMemberQuery] = useState("");
  const visitors = useVillageStore((state) => state.liveVisitors);
  const ownerNames = useMemo(() => buildOwnerNames(data), [data]);
  const plot = data.world.plots.find((candidate) => candidate.id === selectedPlotId);
  const members = data.world.plots.filter((candidate) => candidate.assignedOwnerId && ownerNameFrom(ownerNames, candidate.assignedOwnerId).toLowerCase().includes(memberQuery.trim().toLowerCase())).slice(0, 8);
  // Derived from the world rather than a fixed divisor, so markers stay inside the map
  // whenever the village changes size.
  const mapScale = useMemo(() => {
    const furthest = Math.max(1, ...data.world.neighborhoods.map((entry) => Math.hypot(entry.center.x, entry.center.z)));
    return 42 / furthest;
  }, [data.world.neighborhoods]);
  if (!plot) {
    return (
      <PanelShell title="Village" eyebrow={`${data.world.neighborhoods.length} neighborhoods`}>
        <EmptyState icon={<Search />} title="Pick a place to visit" copy="Select a cottage in the village or use member search." />
        <div className="mini-map" aria-label="Illustrated neighborhood map">
          <i className="map-path path-one" /><i className="map-path path-two" />
          {data.world.neighborhoods.map((neighborhood, index) => <button key={neighborhood.id} aria-label={`Neighborhood ${index + 1}`} onClick={() => useVillageStore.getState().selectPlot(data.world.plots.find((candidate) => candidate.neighborhoodId === neighborhood.id && candidate.assignedOwnerId)?.id ?? null)} style={{ left: `${50 + neighborhood.center.x * mapScale}%`, top: `${50 + neighborhood.center.z * mapScale}%` }}><span>{index + 1}</span></button>)}
          <b><Landmark size={15} /></b>
        </div>
        <label className="field-label" htmlFor="member-search">Find a neighbor</label>
        <div className="search-field"><Search size={17} /><input id="member-search" placeholder="Search member names" value={memberQuery} onChange={(event) => setMemberQuery(event.target.value)} /></div>
        <div className="member-list">
          {members.map((candidate) => (
            <button key={candidate.id} className="member-row" onClick={() => useVillageStore.getState().selectPlot(candidate.id)}>
              <span className="avatar-dot">{ownerNameFrom(ownerNames, candidate.assignedOwnerId).slice(0, 1)}</span>
              <span>{ownerNameFrom(ownerNames, candidate.assignedOwnerId)}</span>
              <ChevronRight size={17} />
            </button>
          ))}
        </div>
      </PanelShell>
    );
  }
  const owner = ownerNameFrom(ownerNames, plot.assignedOwnerId);
  const own = plot.assignedOwnerId === data.member.id;
  const inside = interiorPhase === "entering" || interiorPhase === "open";
  return (
    <PanelShell title={own ? data.property.name : `${owner}'s home`} eyebrow={plot.template}>
      <div className="property-hero">
        <span className="avatar-large">{owner.slice(0, 1)}</span>
        <div><strong>{owner}</strong><span>Neighborhood {plot.neighborhoodId.split("-").at(-1)}</span></div>
      </div>
      <div className="quick-facts">
        <span><House size={17} /> {own ? (data.property.homeType ? getHomeType(data.property.homeType).name : "No home yet") : "Cottage"}</span>
        <span><Leaf size={17} /> {own ? `${data.property.plants.length} plants` : "Garden"}</span>
        <span><PawPrint size={17} /> {own ? `${data.property.pets.length} pets` : "Pets welcome"}</span>
      </div>
      <div className="panel-actions">
        {own && inside && <button className="primary-button" onClick={() => useVillageStore.getState().exitInterior()}><X size={17} /> Step outside</button>}
        {own && !inside && <button className="primary-button" onClick={() => useVillageStore.getState().enterInterior(plot.id)}><House size={17} /> Step inside</button>}
        {!own && (
          <button className="primary-button" onClick={() => { sendPresence({ type: "react", reaction: "heart" }); useVillageStore.getState().showToast(`${owner} will see your heart`, "success"); }}>
            <Heart size={17} /> Leave a heart
          </button>
        )}
        {own && <button className="secondary-button" onClick={() => setPanel("home")}>Edit my home</button>}
        {!own && <button className="secondary-button" onClick={() => setPanel("gifts")}><Gift size={17} /> Send a gift</button>}
      </div>
      <section className="panel-section">
        <h3>Who is here now</h3>
        {/* The presence socket already knows. This used to be four hardcoded letters. */}
        {visitors.length === 0
          ? <p className="panel-note">Nobody else is in the village at the moment.</p>
          : (
            <div className="visitor-faces">
              {visitors.slice(0, 6).map((visitor) => <span key={visitor.memberId} title={visitor.displayName}>{visitor.displayName.slice(0, 1)}</span>)}
              {visitors.length > 6 && <span>+{visitors.length - 6}</span>}
            </div>
          )}
      </section>
    </PanelShell>
  );
}

/**
 * Choosing where you live. All three types unlock together with the first quest and the first
 * pick is free; moving afterwards costs the change fee, shown on every option that is not the
 * one you are in.
 */
function HomeChoice() {
  const data = useVillageStore((state) => state.data);
  const chooseHome = useVillageStore((state) => state.chooseHome);
  const current = data.property.homeType;
  const unlocked = HOME_TYPES.filter((home) => data.ownedItemIds.includes(home.itemId));

  if (!unlocked.length) {
    return (
      <section className="panel-section first">
        <div className="section-heading"><h3>Your home</h3></div>
        <EmptyState
          icon={<House />}
          title="Your parcel is waiting"
          copy="Finish your first quest to claim a home. You will get to pick which one."
          action={<button className="primary-button" onClick={() => useVillageStore.getState().setPanel("quests")}>See today's quests</button>}
        />
      </section>
    );
  }

  return (
    <section className="panel-section first">
      <div className="section-heading">
        <h3>Your home</h3>
        <span>{current ? "Moving costs a change fee" : "Your first home is free"}</span>
      </div>
      <div className="home-choices" role="radiogroup" aria-label="Home type">
        {unlocked.map((home) => {
          const isCurrent = current === home.type;
          return (
            <button
              key={home.type}
              className={`home-choice home-choice-${home.type} ${isCurrent ? "is-current" : ""}`}
              role="radio"
              aria-checked={isCurrent}
              disabled={isCurrent}
              onClick={() => chooseHome(home.type)}
            >
              <span className="home-art" aria-hidden="true"><House size={26} /></span>
              <span className="home-copy">
                <strong>{home.name}</strong>
                <small>{home.description}</small>
              </span>
              <span className="home-action">
                {isCurrent
                  ? <span className="home-current"><Check size={15} /> Current</span>
                  : current
                    ? <MoneyLabel value={HOME_CHANGE_FEE_COPPER} />
                    : <span className="home-free">Move in</span>}
              </span>
            </button>
          );
        })}
      </div>
      {unlocked.length < HOME_TYPES.length && <p className="socket-note">More home types unlock as the village grows.</p>}
    </section>
  );
}

function HomePanel() {
  const data = useVillageStore((state) => state.data);
  const adoptPet = useVillageStore((state) => state.adoptPet);
  const plant = useVillageStore((state) => state.plant);
  const enterInterior = useVillageStore((state) => state.enterInterior);
  const exitInterior = useVillageStore((state) => state.exitInterior);
  const interiorPhase = useVillageStore((state) => state.interiorPhase);
  const [pet, setPet] = useState<PetSpecies>("dog");
  const [petName, setPetName] = useState("Scout");
  const closeHome = () => useVillageStore.getState().setPanel(null);
  // Indoors the panel is about the inside of the home; the yard, the garden and the week's
  // weather all belong to the world outside and would only be noise in here.
  const inside = interiorPhase === "entering" || interiorPhase === "open";
  const plantItems = data.catalog.filter((item) => item.category === "plant").slice(0, 6);
  return (
    <PanelShell
      title={data.property.name}
      eyebrow={data.property.homeType ? getHomeType(data.property.homeType).name : "No home yet"}
      onClose={closeHome}
    >
      <HomeChoice />
      {!inside && <Forecast />}
      {data.property.homeType && (
        <section className="panel-section">
          <div className="section-heading">
            <h3>Inside</h3>
            <span>{inside ? "You are indoors" : "The space your home gives you"}</span>
          </div>
          {/* One way in, because there is one interior. The four-room strip was a promise the
              renderer never kept — a home has an inside, and its size is the home you chose. */}
          <button
            className="secondary-button"
            onClick={() => (inside ? exitInterior() : enterInterior(data.property.plotId))}
          >
            <House size={17} /> {inside ? "Step outside" : "Step inside"}
          </button>
        </section>
      )}
      {inside && <InteriorEditor />}
      {!inside && <section className="panel-section">
        <div className="section-heading"><h3>Pets</h3><span>{data.property.pets.length} of 3</span></div>
        {data.property.pets.map((animal) => (
          <div className="pet-row" key={animal.id}>
            <span className="pet-face"><PawPrint /></span>
            <div><strong>{animal.name}</strong><span>{animal.species}  {animal.affection}% affection</span></div>
            <div className="affection-bar"><i style={{ width: `${animal.affection}%` }} /></div>
          </div>
        ))}
        {data.property.pets.length < 3 && (
          <div className="adopt-form">
            <label className="field-label" htmlFor="pet-species">Adopt a familiar animal</label>
            <select id="pet-species" value={pet} onChange={(event) => setPet(event.target.value as PetSpecies)}>
              {petSpecies.map((species) => <option key={species}>{species}</option>)}
            </select>
            <label className="field-label" htmlFor="pet-name">Pet name</label>
            <input id="pet-name" value={petName} maxLength={24} onChange={(event) => setPetName(event.target.value)} />
            <button className="secondary-button" onClick={() => adoptPet(pet, petName, getCatalogItem(`pet-${pet}`).variants.appearance?.[0] ?? "default")}>
              <Plus size={17} /> Adopt for 5 silver
            </button>
          </div>
        )}
      </section>}
      {!inside && <YardEditor />}
      {!inside && <section className="panel-section">
        <div className="section-heading"><h3>Garden</h3><span>{data.property.plants.length} of 4 beds</span></div>
        <div className="plant-picker">
          {plantItems.map((item) => (
            <button key={item.id} disabled={data.property.plants.length >= 4} onClick={() => plant(item.id)}>
              <Leaf size={18} /><span>{item.name}</span><MoneyLabel value={item.priceCopper} />
            </button>
          ))}
        </div>
      </section>}
    </PanelShell>
  );
}

function QuestsPanel() {
  const quests = useVillageStore((state) => state.data.activeQuests);
  const completeQuest = useVillageStore((state) => state.completeQuest);
  const previewMode = !isLive();
  return (
    <PanelShell title="Today's quests" eyebrow="Tuesday in Tribe">
      <p className="panel-intro">Complete a real conversation prompt in Discord. The bot verifies your post before progress is awarded.</p>
      <div className="quest-list">
        {quests.map((quest, index) => (
          <article key={quest.id} className="quest-item">
            <div className="quest-number">{index + 1}</div>
            <div>
              <span className="quest-kind">{quest.kind} quest</span>
              <h3>{quest.prompt}</h3>
              <div className="quest-meta"><MoneyLabel value={quest.rewardCopper} /><span>+{quest.projectEffort} town effort</span></div>
              <div className="quest-actions">
                <button className="primary-button" onClick={() => void returnToDiscord()}>Return to Discord</button>
                {previewMode && <button className="text-button" onClick={() => completeQuest(quest.id)}>Preview reward</button>}
              </div>
            </div>
          </article>
        ))}
      </div>
      <div className="consistency">
        <div><strong>Weekly rhythm</strong><span>Participate on three different days</span></div>
        <div className="day-marks"><i className="done" /><i className="done" /><i /><i /><i /><i /><i /></div>
      </div>
    </PanelShell>
  );
}

function ShopPanel() {
  const data = useVillageStore((state) => state.data);
  const purchase = useVillageStore((state) => state.purchase);
  const [category, setCategory] = useState<ItemCategory>("furniture");
  const [query, setQuery] = useState("");
  const items = useMemo(() => data.catalog.filter((item) => item.category === category && item.name.toLowerCase().includes(query.toLowerCase())), [data.catalog, category, query]);
  return (
    <PanelShell title="Village shop" eyebrow={`${data.catalog.length} rewards`}>
      <div className="search-field"><Search size={17} /><input aria-label="Search rewards" placeholder="Search rewards" value={query} onChange={(event) => setQuery(event.target.value)} /></div>
      <div className="category-tabs" role="tablist">
        {shopCategories.map((entry) => (
          <button key={entry.id} role="tab" aria-selected={category === entry.id} className={category === entry.id ? "is-active" : ""} onClick={() => setCategory(entry.id)}>{entry.label}</button>
        ))}
      </div>
      <div className="shop-list">
        {items.map((catalogItem) => {
          const owned = data.ownedItemIds.includes(catalogItem.id);
          return (
            <article className="shop-item" key={catalogItem.id}>
              <div className={`item-art item-art-${catalogItem.category}`}><Sparkles size={22} /></div>
              <div className="shop-copy"><span className={`rarity rarity-${catalogItem.rarity}`}>{catalogItem.rarity}</span><h3>{catalogItem.name}</h3><p>{catalogItem.description}</p></div>
              <button className={owned ? "owned-button" : "buy-button"} disabled={owned || Boolean(catalogItem.achievement)} onClick={() => purchase(catalogItem.id)}>
                {catalogItem.achievement ? <ShieldCheck size={17} /> : owned ? <Check size={17} /> : <MoneyLabel value={catalogItem.priceCopper} />}
                <span>{catalogItem.achievement ? "Achievement" : owned ? "Owned" : "Buy"}</span>
              </button>
            </article>
          );
        })}
      </div>
    </PanelShell>
  );
}

function CollectionPanel() {
  const data = useVillageStore((state) => state.data);
  const owned = data.catalog.filter((item) => data.ownedItemIds.includes(item.id));
  return (
    <PanelShell title="Collection" eyebrow={`${owned.length} owned`}>
      <div className="collection-summary">
        {Object.entries(CATALOG_COUNTS).map(([category, total]) => {
          const count = owned.filter((item) => item.category === category).length;
          return <div key={category}><strong>{count}</strong><span>{category}</span><small>of {total}</small></div>;
        })}
      </div>
      <div className="owned-grid">
        {owned.map((item) => <div key={item.id} className="owned-tile"><Sparkles size={20} /><span>{item.name}</span><small>{item.category}</small></div>)}
      </div>
    </PanelShell>
  );
}

function GiftsPanel() {
  const data = useVillageStore((state) => state.data);
  const sendGift = useVillageStore((state) => state.sendGift);
  // The real street, sorted by name. This was four hardcoded demo IDs, which meant that in the
  // Tribe guild there was nobody a gift could actually be sent to.
  const recipients = useMemo(
    () => [...data.neighbours].map((neighbour) => ({ id: neighbour.memberId, name: neighbour.displayName })).sort((left, right) => left.name.localeCompare(right.name)),
    [data.neighbours]
  );
  const [recipient, setRecipient] = useState("");
  const chosen = recipient || recipients[0]?.id || "";
  const giftable = data.catalog.filter((item) => item.giftable && !item.achievement).slice(0, 12);
  return (
    <PanelShell title="Gifts" eyebrow="Make someone's path brighter">
      {recipients.length === 0 ? (
        <EmptyState icon={<Gift />} title="No neighbors yet" copy="Once other members have homes in the village, you can send them something." />
      ) : (
        <>
      <div className="gift-recipient">
        <label className="field-label" htmlFor="gift-recipient">Send to</label>
        <select id="gift-recipient" value={chosen} onChange={(event) => setRecipient(event.target.value)}>
          {recipients.map((entry) => <option key={entry.id} value={entry.id}>{entry.name}</option>)}
        </select>
      </div>
      <div className="gift-list">
        {giftable.map((item) => (
          <button key={item.id} onClick={() => sendGift(item.id, chosen)}>
            <Gift size={18} /><span>{item.name}</span><MoneyLabel value={item.priceCopper} /><Send size={16} />
          </button>
        ))}
      </div>
        </>
      )}
      <section className="panel-section">
        <h3>Gift history</h3>
        {data.gifts.map((gift) => <div key={gift.id} className="history-row"><Gift size={17} /><span>{getCatalogItem(gift.itemId).name}</span><small>{gift.status}</small></div>)}
      </section>
    </PanelShell>
  );
}

function ProjectPanel() {
  const project = useVillageStore((state) => state.data.project);
  const donate = useVillageStore((state) => state.donateToProject);
  const ratio = projectRatio(project);
  const stage = projectStageIndex(project);
  return (
    <PanelShell title={project.name} eyebrow="Community project">
      <div className="project-mark"><span>{Math.round(ratio * 100)}%</span><i style={{ "--project-progress": `${ratio * 360}deg` } as React.CSSProperties} /></div>
      <p className="panel-intro">{project.description}</p>
      <div className="project-progress"><i style={{ width: `${ratio * 100}%` }} /><span>{project.progress} of {project.goal} effort</span></div>
      <div className="project-stages">
        {project.stages.map((entry, index) => (
          <div key={entry.name} className={index <= stage ? "complete" : ""}><span>{index <= stage ? <Check size={15} /> : index + 1}</span><div><strong>{entry.name}</strong><small>{entry.description}</small></div></div>
        ))}
      </div>
      <section className="panel-section contribution-box">
        <h3>Add a little more</h3>
        <p>Every 10 copper adds one effort. Quest contributions happen automatically.</p>
        <div className="donation-buttons"><button onClick={() => donate(10)}>10 copper</button><button onClick={() => donate(50)}>50 copper</button><button onClick={() => donate(100)}>1 silver</button></div>
      </section>
    </PanelShell>
  );
}

type AdminTab = "quests" | "projects" | "reports" | "audit";

interface ReviewQueues {
  names: Array<{ id: string; member_id: string; entity_type: string; proposed_name: string; created_at: string }>;
  helpful: Array<{ id: string; confirmer_id: string; recipient_id: string; created_at: string }>;
  drafts: Array<{ id: string; prompt: string; channel_group: string; verification: string; created_at: string }>;
}

interface AuditFeed {
  actions: Array<{ actor_id: string; action: string; target_id: string; created_at: string }>;
  ledger: Array<{ member_id: string; amount_copper: number; kind: string; created_at: string }>;
  openReviews: number;
}

const relativeTime = (iso: string) => {
  const minutes = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60_000));
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} minutes ago`;
  const hours = Math.round(minutes / 60);
  return hours < 24 ? `${hours} hours ago` : `${Math.round(hours / 24)} days ago`;
};

/** A blank slate for the demo build, which has no Worker to ask. */
const emptyQueues: ReviewQueues = { names: [], helpful: [], drafts: [] };

function QuestDraftTab() {
  const draft = useVillageStore((state) => state.questDraft);
  const setDraft = useVillageStore((state) => state.setQuestDraft);
  const saveDraft = useVillageStore((state) => state.saveQuestDraft);
  const channelGroup = useVillageStore((state) => state.questChannelGroup);
  const setChannelGroup = useVillageStore((state) => state.setQuestChannelGroup);
  const verification = useVillageStore((state) => state.questVerification);
  const setVerification = useVillageStore((state) => state.setQuestVerification);
  const result = validateQuestCopy(draft);
  const clean = result.valid && result.warnings.length === 0;
  return (
    <section className="panel-section first">
      <h3>Write a quest</h3>
      <label className="field-label" htmlFor="quest-copy">Quest copy</label>
      <textarea id="quest-copy" value={draft} maxLength={180} onChange={(event) => setDraft(event.target.value)} />
      <div className={`copy-check ${clean ? "valid" : "warning"}`}>
        {clean ? <Check size={17} /> : <ShieldCheck size={17} />}
        <span>{result.errors[0] ?? result.warnings[0] ?? "Natural language check passed"}</span>
      </div>
      {/* Controlled, so the quest is saved against the channel and verification that were picked.
          These used to be uncontrolled and the save sent "general" and "message" regardless. */}
      <div className="form-grid">
        <label htmlFor="quest-channel">Channel group
          <select id="quest-channel" value={channelGroup} onChange={(event) => setChannelGroup(event.target.value)}>
            {["general", "clips", "gaming", "phasmophobia", "books", "lifestyle", "lfg", "helpful"].map((group) => <option key={group} value={group}>{group}</option>)}
          </select>
        </label>
        <label htmlFor="quest-verification">Verification
          <select id="quest-verification" value={verification} onChange={(event) => setVerification(event.target.value)}>
            {["message", "reply", "attachment", "peer", "voice_confirmed"].map((type) => <option key={type} value={type}>{type}</option>)}
          </select>
        </label>
      </div>
      <button className="primary-button full" onClick={saveDraft} disabled={!clean}>Save for review</button>
      <p className="panel-note">A saved quest waits in the review queue. Nothing reaches the live rotation until it is approved.</p>
    </section>
  );
}

function ProjectsTab() {
  const project = useVillageStore((state) => state.data.project);
  const toast = useVillageStore((state) => state.showToast);
  const [choices, setChoices] = useState([
    { blueprintId: "pet-park", name: "Pet park", picked: true },
    { blueprintId: "village-library", name: "Village library", picked: true },
    { blueprintId: "greenhouse", name: "Greenhouse", picked: false },
    { blueprintId: "boat-house", name: "Boat house", picked: false }
  ]);
  const [closesAt, setClosesAt] = useState(() => new Date(Date.now() + 7 * 86_400_000).toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const picked = choices.filter((choice) => choice.picked);

  const openPoll = async () => {
    if (picked.length < 2) return toast("A poll needs at least two blueprints", "error");
    setBusy(true);
    try {
      const sent = await apiMutation("/api/v1/admin/project-polls", {
        choices: picked.map(({ blueprintId, name }) => ({ blueprintId, name })),
        opensAt: new Date().toISOString(),
        closesAt: new Date(`${closesAt}T12:00:00Z`).toISOString()
      });
      toast(sent ? "The poll is open" : "Polls open once the village is connected", sent ? "success" : "info");
    } catch (error) {
      toast(error instanceof Error ? error.message : "That poll could not be opened", "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <section className="panel-section first">
        <div className="section-heading"><h3>Current project</h3><span>{Math.round(projectRatio(project) * 100)}%</span></div>
        <div className="project-progress"><i style={{ width: `${projectRatio(project) * 100}%` }} /><span>{project.progress} of {project.goal} effort</span></div>
        <p className="panel-note">The goal is fixed when a project is created and progress never passes it.</p>
      </section>
      <section className="panel-section">
        <h3>Open the next poll</h3>
        <div className="poll-options">
          {choices.map((choice, index) => (
            <label key={choice.blueprintId}>
              <input
                type="checkbox"
                checked={choice.picked}
                onChange={() => setChoices(choices.map((entry, at) => at === index ? { ...entry, picked: !entry.picked } : entry))}
              />
              {choice.name}
            </label>
          ))}
        </div>
        <label className="field-label" htmlFor="poll-closes">Closes on</label>
        <input id="poll-closes" type="date" value={closesAt} onChange={(event) => setClosesAt(event.target.value)} />
        <button className="primary-button full" onClick={() => void openPoll()} disabled={busy || picked.length < 2}>Open poll</button>
        <p className="panel-note">The winner joins the queue when the poll closes and starts once the current project finishes.</p>
      </section>
    </>
  );
}

function ReportsTab({ queues, reload }: { queues: ReviewQueues; reload: () => void }) {
  const toast = useVillageStore((state) => state.showToast);
  const [busy, setBusy] = useState(false);

  const resolve = async (kind: "name" | "helpful" | "draft", id: string, decision: "approve" | "reject") => {
    setBusy(true);
    try {
      const sent = await apiMutation("/api/v1/admin/reviews/resolve", { kind, id, decision });
      toast(sent ? `Marked ${decision === "approve" ? "approved" : "rejected"}` : "Reviews resolve once the village is connected", sent ? "success" : "info");
      reload();
    } catch (error) {
      toast(error instanceof Error ? error.message : "That review could not be resolved", "error");
    } finally {
      setBusy(false);
    }
  };

  const total = queues.names.length + queues.helpful.length + queues.drafts.length;
  if (!total) {
    return <EmptyState icon={<ShieldCheck />} title="Nothing waiting" copy="Flagged names, repeat recognitions, and quest drafts all appear here." />;
  }

  return (
    <>
      {queues.names.length > 0 && (
        <section className="panel-section first">
          <h3>Names waiting</h3>
          {queues.names.map((review) => (
            <div key={review.id} className="review-row">
              <div><strong>{review.proposed_name}</strong><small>{review.entity_type} · {relativeTime(review.created_at)}</small></div>
              <div className="review-actions">
                <button className="secondary-button" disabled={busy} onClick={() => void resolve("name", review.id, "reject")}>Reject</button>
                <button className="primary-button" disabled={busy} onClick={() => void resolve("name", review.id, "approve")}>Approve</button>
              </div>
            </div>
          ))}
        </section>
      )}
      {queues.helpful.length > 0 && (
        <section className="panel-section">
          <h3>Recognitions to check</h3>
          <p className="panel-note">These pairs have recognized each other more than twice this week.</p>
          {queues.helpful.map((entry) => (
            <div key={entry.id} className="review-row">
              <div><strong>{entry.confirmer_id} recognized {entry.recipient_id}</strong><small>{relativeTime(entry.created_at)}</small></div>
              <div className="review-actions">
                <button className="secondary-button" disabled={busy} onClick={() => void resolve("helpful", entry.id, "reject")}>Decline</button>
                <button className="primary-button" disabled={busy} onClick={() => void resolve("helpful", entry.id, "approve")}>Award</button>
              </div>
            </div>
          ))}
        </section>
      )}
      {queues.drafts.length > 0 && (
        <section className="panel-section">
          <h3>Quest drafts</h3>
          {queues.drafts.map((entry) => (
            <div key={entry.id} className="review-row">
              <div><strong>{entry.prompt}</strong><small>{entry.channel_group} · {entry.verification} · {relativeTime(entry.created_at)}</small></div>
              <div className="review-actions">
                <button className="secondary-button" disabled={busy} onClick={() => void resolve("draft", entry.id, "reject")}>Reject</button>
                <button className="primary-button" disabled={busy} onClick={() => void resolve("draft", entry.id, "approve")}>Approve</button>
              </div>
            </div>
          ))}
        </section>
      )}
    </>
  );
}

function AuditTab({ feed }: { feed: AuditFeed | null }) {
  if (!feed) return <EmptyState icon={<ShieldCheck />} title="No audit trail here" copy="Moderator actions and coin movement appear once the village is connected." />;
  const lastCoin = feed.ledger[0];
  return (
    <>
      <section className="panel-section first">
        <div className="section-heading"><h3>Audit health</h3><span className={feed.openReviews ? "status-warn" : "status-good"}>{feed.openReviews ? `${feed.openReviews} waiting` : "All clear"}</span></div>
        <div className="audit-row"><Coins size={17} /><span>Last coin transaction</span><small>{lastCoin ? relativeTime(lastCoin.created_at) : "none yet"}</small></div>
        <div className="audit-row"><ShieldCheck size={17} /><span>Open reviews</span><small>{feed.openReviews}</small></div>
      </section>
      <section className="panel-section">
        <h3>Recent moderator actions</h3>
        {feed.actions.length === 0
          ? <p className="panel-note">Nothing recorded yet.</p>
          : feed.actions.map((entry, index) => (
            <div key={`${entry.created_at}-${index}`} className="audit-row">
              <ShieldCheck size={17} />
              <span>{entry.action.replaceAll("_", " ")}</span>
              <small>{relativeTime(entry.created_at)}</small>
            </div>
          ))}
      </section>
      <section className="panel-section">
        <h3>Recent coin movement</h3>
        {feed.ledger.map((entry, index) => (
          <div key={`${entry.created_at}-${index}`} className="audit-row">
            <Coins size={17} />
            <span>{entry.kind.replaceAll("_", " ")}</span>
            <small>{entry.amount_copper > 0 ? `+${entry.amount_copper}` : entry.amount_copper}</small>
          </div>
        ))}
      </section>
    </>
  );
}

function AdminPanel() {
  const [tab, setTab] = useState<AdminTab>("quests");
  const [queues, setQueues] = useState<ReviewQueues>(emptyQueues);
  const [audit, setAudit] = useState<AuditFeed | null>(null);
  const toast = useVillageStore((state) => state.showToast);

  // Loaded when the tab that needs it is opened, rather than on every panel open. These are
  // moderator reads against live tables, and most visits to this panel are to write a quest.
  const [reloadKey, setReloadKey] = useState(0);
  useEffect(() => {
    if (!isLive()) return;
    let cancelled = false;
    const load = async () => {
      try {
        if (tab === "reports") {
          const result = await apiQuery<ReviewQueues>("/api/v1/admin/reviews");
          if (!cancelled && result) setQueues(result);
        }
        if (tab === "audit") {
          const result = await apiQuery<AuditFeed>("/api/v1/admin/audit");
          if (!cancelled && result) setAudit(result);
        }
      } catch (error) {
        if (!cancelled) toast(error instanceof Error ? error.message : "That view could not be loaded", "error");
      }
    };
    void load();
    return () => { cancelled = true; };
  }, [tab, reloadKey, toast]);

  const tabs: Array<{ id: AdminTab; label: string }> = [
    { id: "quests", label: "Quests" },
    { id: "projects", label: "Projects" },
    { id: "reports", label: "Reports" },
    { id: "audit", label: "Audit" }
  ];

  return (
    <PanelShell title="Village tools" eyebrow="Moderator only">
      <div className="admin-tabs" role="tablist">
        {tabs.map((entry) => (
          <button key={entry.id} role="tab" aria-selected={tab === entry.id} className={tab === entry.id ? "is-active" : ""} onClick={() => setTab(entry.id)}>
            {entry.label}
            {entry.id === "reports" && audit?.openReviews ? <i className="tab-count">{audit.openReviews}</i> : null}
          </button>
        ))}
      </div>
      {tab === "quests" && <QuestDraftTab />}
      {tab === "projects" && <ProjectsTab />}
      {tab === "reports" && <ReportsTab queues={queues} reload={() => setReloadKey((key) => key + 1)} />}
      {tab === "audit" && <AuditTab feed={audit} />}
    </PanelShell>
  );
}

export function ActivePanel({ panel }: { panel: PanelId }) {
  switch (panel) {
    case "village": return <VillagePanel />;
    case "home": return <HomePanel />;
    case "quests": return <QuestsPanel />;
    case "shop": return <ShopPanel />;
    case "collection": return <CollectionPanel />;
    case "gifts": return <GiftsPanel />;
    case "project": return <ProjectPanel />;
    case "admin": return <AdminPanel />;
    default: return null;
  }
}
